import axios from "axios";
import lodash from "lodash";
import moment from "moment";
import { observable, computed, action, toJS } from "mobx";

const PER_PAGE = 10;

class SubRequestStorage {
  @observable __data = [];
  @observable __location = null;
  @observable __position = null;
  @observable __loading = false;
  @observable __page = null;
  @observable __currentPage = 1;
  @observable __totalPages = 0;

  __currentUser = window.Application.currentUser;
  __currentUserHasScheduleManageAccess = false;
  __locations = [];
  __positions = [];
  __currentUserPositions = [];

  constructor(
    currentUserHasScheduleManageAccess,
    locations,
    positions,
    currentUserPositions,
    page
  ) {
    this.__currentUserHasScheduleManageAccess = currentUserHasScheduleManageAccess;
    this.__locations = locations;
    this.__positions = positions;
    this.__currentUserPositions = currentUserPositions;
    this.__page = page;

    this.__locations.unshift({ id: "", value: "All Locations" });
    this.__location = locations[0].id.toString();

    this.__positions.unshift({ id: "", value: "All Positions" });
    this.__currentUserPositions.unshift({ id: "", value: "All Positions" });
    this.__position = positions[0].id.toString();

    this.load();
  }

  url() {
    const active = this.__page === "open" ? "1" : "0";
    const processed = this.__page === "processed" ? "1" : "0";
    const order = this.__page === "processed" ? "desc" : "asc";

    const params = [
      `page=${this.__currentPage}`,
      `active=${active}`,
      `processed=${processed}`,
      `order_dir=${order}`
    ];

    if (active === "1" && processed === "0") {
      params.push(`hiding_previous=true`);
    }

    if (this.__location) {
      params.push(`location_id=${this.__location}`);
    } else {
      const ids = this.__locations
        .filter(item => item.id)
        .map(item => item.id)
        .join(",");
      params.push(`location_id=${ids}`);
    }

    if (this.__position) {
      params.push(`position_id=${this.__position}`)
    } else {
      const ids = this.filteringPositions
        .filter(item => item.id)
        .map(item => item.id)
        .join(",");
      params.push(`position_id=${ids}`);
    }

    return `/sub_requests.json?${params.join("&")}`;
  }

  get currentUserHasScheduleManageAccess() {
    return this.__currentUserHasScheduleManageAccess
  }

  get locations() {
    return toJS(this.__locations);
  }

  get positions() {
    return toJS(this.__positions);
  }

  get page() {
    return this.__page;
  }

  get filteringPositions() {
    return toJS(this.__currentUserHasScheduleManageAccess ? this.__positions : this.__currentUserPositions);
  }

  @computed
  get currentPage() {
    return this.__currentPage;
  }

  @computed
  get totalPages() {
    return this.__totalPages;
  }

  @computed
  get location() {
    return toJS(this.__location);
  }

  @computed
  get position() {
    return toJS(this.__position);
  }

  @computed
  get loading() {
    return this.__loading;
  }

  @computed
  get data() {
    return toJS(this.__data);
  }

  @computed
  get exportUrl() {
    const active = this.__page === "open" ? "1" : "0";
    const processed = this.__page === "processed" ? "1" : "0";

    const params = [`active=${active}`, `processed=${processed}`];

    if (this.__location) {
      params.push(`location_id=${this.__location}`);
    }

    return `/export/sub_requests?${params.join("&")}`;
  }

  @action
  setLocation = e => {
    this.__location = e.target.value;

    this.__currentPage = 1;
    this.load();
  };

  @action
  setPosition = e => {
    this.__position = e.target.value;

    this.__currentPage = 1;
    this.load();
  };

  @action
  changePage = n => {
    this.__currentPage = this.__currentPage + n;
    this.load();
  };

  @action
  accept = request => {
    const data = {
      active: false,
      processed: false,
      sub_user_id: this.__currentUser.id
    };

    axios.put(`/sub_requests/${request.id}.json`, data).then(() => {
      const idx = lodash.findIndex(this.__data, { id: request.id });
      this.__data.splice(idx, 1);

      toastr.success("Shift accepted");
    });
  };

  @action
  destroy = request => {
    axios.delete(`/sub_requests/${request.id}.json`).then(() => {
      const idx = lodash.findIndex(this.__data, { id: request.id });
      this.__data.splice(idx, 1);
    });
  };

  @action
  approve = (request, adminNote) => {
    const data = {
      id: request.id,
      approved: true,
      processed: true,
      processed_on: moment(),
      processed_by_user_id: this.__currentUser.id,
      processed_by_first_name: this.__currentUser.first_name,
      processed_by_last_name: this.__currentUser.last_name,
      admin_note: adminNote
    };

    axios.put(`/sub_requests/${request.id}.json`, data).then(() => {
      const idx = lodash.findIndex(this.__data, { id: request.id });
      this.__data.splice(idx, 1);

      toastr.success("Sub Request approved");
    });
  };

  @action
  deny = (request, adminNote) => {
    const data = {
      id: request.id,
      approved: false,
      processed: true,
      processed_on: moment(),
      processed_by_user_id: this.__currentUser.id,
      processed_by_first_name: this.__currentUser.first_name,
      processed_by_last_name: this.__currentUser.last_name,
      admin_note: adminNote
    };

    axios.put(`/sub_requests/${request.id}.json`, data).then(() => {
      axios.post("/sub_requests", {
        shift_id: request.shift_id,
        user_id: request.user_id,
        reason: request.reason
      });

      const idx = lodash.findIndex(this.__data, { id: request.id });
      this.__data.splice(idx, 1);

      toastr.error("Sub Request denied");
    });
  };

  @action
  returnToManagement = request => {
    const data = {
      active: false,
      processed: false,
      processed_on: null,
      processed_by_user_id: null,
      processed_by_first_name: null,
      processed_by_last_name: null
    };

    axios.put(`/sub_requests/${request.id}.json`, data).then(() => {
      const idx = lodash.findIndex(this.__data, { id: request.id });
      this.__data.splice(idx, 1);

      toastr.info("Sub Request returned to management");
    });
  };

  @action
  load(page) {
    if (this.__loading) {
      return;
    }

    this.__loading = true;

    axios.get(this.url(page)).then(resp => {
      this.__data = this.buildData(resp.data);
      this.__totalPages = Math.ceil(parseInt(resp.headers.total) / PER_PAGE);
      this.__loading = false;
    });
  }

  buildData(data) {
    return data.filter(item => item.shift).map(item => {
      const location = this.__locations.filter(
        l => l.id === item.shift.location_id
      );
      const position = this.__positions.filter(
        p => p.id === item.shift.position_id
      );

      if (location.length > 0) {
        item.locationName = location[0].value;
      }

      if (position.length > 0) {
        item.positionName = position[0].value;
      }

      return item;
    });
  }
}

export default SubRequestStorage;
